import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface BasicInfoSectionProps {
  providerInfo: any;
  form?: any;
  setForm?: React.Dispatch<React.SetStateAction<any>>;
  saving?: boolean;
  readonly?: boolean;
}

export const BasicInfoSection = ({
  providerInfo,
  form,
  setForm,
  saving = false,
  readonly = false,
}: BasicInfoSectionProps) => {
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setForm) {
      setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  if (readonly) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Basic Information</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Provider Name</Label>
            <p className="text-base mt-1">{providerInfo.name || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Contact Email</Label>
            <p className="text-base mt-1">{providerInfo.contact_email || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Contact Phone</Label>
            <p className="text-base mt-1">{providerInfo.contact_phone || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Website</Label>
            <p className="text-base mt-1">
              {providerInfo.website ? (
                <a href={providerInfo.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {providerInfo.website}
                </a>
              ) : (
                <span className="text-muted-foreground italic">Not set</span>
              )}
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Primary Contact Name</Label>
            <p className="text-base mt-1">{providerInfo.primary_contact_name || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Secondary Contact Email</Label>
            <p className="text-base mt-1">{providerInfo.secondary_contact_email || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Years in Business</Label>
            <p className="text-base mt-1">{providerInfo.years_in_business !== null ? providerInfo.years_in_business : <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Employee Range</Label>
            <p className="text-base mt-1">{providerInfo.employee_range || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Headquarters State</Label>
            <p className="text-base mt-1">{providerInfo.hq_state || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Basic Information</h3>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label htmlFor="name">Provider Name *</Label>
          <Input
            id="name"
            name="name"
            value={form?.name || ''}
            onChange={handleFieldChange}
            placeholder="Enter your provider's legal name"
            disabled={saving}
            required
          />
        </div>
        <div>
          <Label htmlFor="contact_email">Contact Email *</Label>
          <Input
            id="contact_email"
            name="contact_email"
            type="email"
            value={form?.contact_email || ''}
            onChange={handleFieldChange}
            placeholder="Contact email"
            disabled={saving}
            required
          />
        </div>
        <div>
          <Label htmlFor="contact_phone">Contact Phone</Label>
          <Input
            id="contact_phone"
            name="contact_phone"
            type="tel"
            value={form?.contact_phone || ''}
            onChange={handleFieldChange}
            placeholder="(555) 123-4567"
            disabled={saving}
          />
        </div>
        <div>
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            name="website"
            type="url"
            value={form?.website || ''}
            onChange={handleFieldChange}
            placeholder="https://www.example.com"
            disabled={saving}
          />
        </div>
        <div>
          <Label htmlFor="primary_contact_name">Primary Contact Name</Label>
          <Input
            id="primary_contact_name"
            name="primary_contact_name"
            value={form?.primary_contact_name || ''}
            onChange={handleFieldChange}
            placeholder="John Doe"
            disabled={saving}
          />
        </div>
        <div>
          <Label htmlFor="secondary_contact_email">Secondary Contact Email</Label>
          <Input
            id="secondary_contact_email"
            name="secondary_contact_email"
            type="email"
            value={form?.secondary_contact_email || ''}
            onChange={handleFieldChange}
            placeholder="secondary@example.com"
            disabled={saving}
          />
        </div>
        <div>
          <Label htmlFor="years_in_business">Years in Business *</Label>
          <Input
            id="years_in_business"
            name="years_in_business"
            type="number"
            min={0}
            value={form?.years_in_business || ''}
            onChange={handleFieldChange}
            placeholder="Years"
            disabled={saving}
            required
          />
        </div>
        <div>
          <Label htmlFor="employee_range">Employee Range *</Label>
          <Input
            id="employee_range"
            name="employee_range"
            value={form?.employee_range || ''}
            onChange={handleFieldChange}
            placeholder="e.g. 1-99"
            disabled={saving}
            required
          />
        </div>
        <div>
          <Label htmlFor="hq_state">Headquarters State</Label>
          <Input
            id="hq_state"
            name="hq_state"
            value={form?.hq_state || ''}
            onChange={handleFieldChange}
            placeholder="e.g. CA, NY, TX"
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};